import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
  Box,
  Paper,
  createTheme,
  ThemeProvider,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  MdExpandMore,
  MdChevronRight,
  MdVisibility,
  MdAdd,
  MdUploadFile,
  MdClear,
  MdDelete,
} from "react-icons/md";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, getToken, toastError } from "../../utils/util";
import { toast } from "react-toastify";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";

function ContactDatabase() {
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 30,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Filtering states
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  // View contact dialog
  const [openView, setOpenView] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  // Add contact dialog
  const [openAdd, setOpenAdd] = useState(false);
  const [newContact, setNewContact] = useState({
    Category: "",
    Subcategory: "",
    Name: "",
    Phone: "",
  });
  // Edit contact dialog
  const [openEdit, setOpenEdit] = useState(false);
  const [editContact, setEditContact] = useState(null);
  // Import dialog
  const [openImport, setOpenImport] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [mapping, setMapping] = useState({});
  // Custom column dialog
  const [openCustomColumn, setOpenCustomColumn] = useState(false);
  const [customColumn, setCustomColumn] = useState("");
  const [currentExcelCol, setCurrentExcelCol] = useState("");
  // Loading states
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedSubs.size > 0) {
      loadContacts();
    } else {
      setContacts([]);
      setPagination({
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 30,
        hasNextPage: false,
        hasPreviousPage: false,
      });
    }
  }, [selectedSubs, pagination.currentPage]);

  const loadCategories = async () => {
    try {
      const catsRes = await axios.get(
        `${API_ENDPOINT}/user/contacts-categories`,
        getHeader()
      );
      setCategories(catsRes.data);

      const subsMap = {};
      for (const cat of catsRes.data) {
        subsMap[cat._id] = {};
        const subsRes = await axios.get(
          `${API_ENDPOINT}/user/contacts-subcategories/${cat._id}`,
          getHeader()
        );
        subsRes.data.forEach((sub) => {
          subsMap[cat._id][sub._id] = {
            name: sub.name,
            columns: sub.columns || ["Name", "Phone"],
          };
        });
      }
      setSubcategories(subsMap);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadContacts = async () => {
    try {
      const subCatIds = [...selectedSubs].join(",");
      const res = await axios.get(`${API_ENDPOINT}/user/get-contacts-db`, {
        ...getHeader(),
        params: {
          subCatIds,
          page: pagination.currentPage,
          limit: pagination.itemsPerPage,
        },
      });
      setContacts(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error("Error loading contacts:", error);
      alert(error.response?.data?.message || "Error loading contacts");
    }
  };

  const handleAddContact = async () => {
    if (!newContact.Name || !newContact.Phone || !newContact.Subcategory) {
      alert("Name, Phone, and Subcategory are mandatory fields.");
      return;
    }
    try {
      const res = await axios.post(
        `${API_ENDPOINT}/user/add-contacts/${newContact.Subcategory}`,
        newContact,
        getHeader()
      );
      setContacts([...contacts, { ...newContact, _id: res.data._id }]);
      setOpenAdd(false);
      setNewContact({ Category: "", Subcategory: "", Name: "", Phone: "" });
      alert(res.data.message);
    } catch (error) {
      console.error("Error adding contact:", error);
      alert(error.response?.data?.message || "Error adding contact");
    }
  };

  const handleEditContact = async () => {
    if (!editContact.Name || !editContact.Phone || !editContact.Subcategory) {
      toast.error("Name, Phone, and Subcategory are mandatory fields.");
      return;
    }
    try {
      const { _id, ...remainContact } = editContact;
      const res = await axios.post(
        `${API_ENDPOINT}/user/add-contacts/${editContact.subCategoryId}`,
        { ...remainContact, editId: _id },
        getHeader()
      );
      setContacts(
        contacts.map((c) =>
          c._id === editContact._id ? { ...editContact } : c
        )
      );
      setOpenEdit(false);
      setEditContact(null);
      alert(res.data.message || "Updated Successfully!");
    } catch (error) {
      console.error("Error editing contact:", error);
      alert(error.response?.data?.message || "Error editing contact");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?"))
      return;
    try {
      await axios.delete(
        `${API_ENDPOINT}/user/delete-contacts/${contactId}`,
        getHeader()
      );
      setContacts(contacts.filter((c) => c._id !== contactId));
      alert("Contact deleted successfully!");
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert(error.response?.data?.message || "Error deleting contact");
    }
  };

  const handleImport = async () => {
    if (excelData.length < 2 || selectedSubs.size !== 1) {
      toast.error("Invalid excelData or selectedSubs:");
      return;
    }
    const [subId] = [...selectedSubs];
    const headers = excelData[0];
    const nameMapped = Object.values(mapping).includes("Name");
    const phoneMapped = Object.values(mapping).includes("Phone");
    if (!nameMapped || !phoneMapped) {
      toast.error("Name and Phone are mandatory fields and must be mapped.");
      return;
    }

    const mappedData = excelData.slice(1).map((row) => {
      const contact = {};
      headers.forEach((header, index) => {
        const mappedField = mapping[header];
        if (mappedField) {
          contact[mappedField] = row[index] || "";
        }
      });
      contact.Subcategory = subId;
      return contact;
    });

    setIsImporting(true);
    try {
      const res = await axios.post(
        `${API_ENDPOINT}/user/import-contacts-db/${subId}`,
        { contacts: mappedData },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      setOpenImport(false);
      setExcelData([]);
      setMapping({});
      toast.success(res.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleSelectCustomColumn = (excelCol) => {
    setCurrentExcelCol(excelCol);
    setOpenCustomColumn(true);
  };

  const handleAddCustomColumn = async () => {
    if (!customColumn || selectedSubs.size !== 1) return;
    const [selectedSubId] = [...selectedSubs];

    try {
      const res = await axios.post(
        `${API_ENDPOINT}/user/add-custom-column/${selectedSubId}`,
        { column: customColumn },
        getHeader()
      );
      const updatedSub = res.data;
      const catId = updatedSub.categoryId;
      setSubcategories({
        ...subcategories,
        [catId]: {
          ...subcategories[catId],
          [selectedSubId]: {
            name: updatedSub.name,
            columns: updatedSub.columns,
          },
        },
      });
      setMapping({ ...mapping, [currentExcelCol]: customColumn });
      setOpenCustomColumn(false);
      setCustomColumn("");
      setCurrentExcelCol("");
      toast.success(res.data.message);
    } catch (error) {
      toastError(error);
    }
  };

  const toggleCategoryExpand = (catId) => {
    setExpandedCategories((prev) =>
      prev.includes(catId) ? prev.filter((c) => c !== catId) : [...prev, catId]
    );
  };

  const toggleSubSelect = (subId) => {
    setSelectedSubs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subId)) {
        newSet.delete(subId);
      } else {
        newSet.add(subId);
      }
      return newSet;
    });
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setOpenView(true);
  };

  const handleEditContactDialog = (contact) => {
    setEditContact({ ...contact });
    setOpenEdit(true);
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const getDisplayColumns = () => {
    return ["Name", "Category", "Subcategory"];
  };

  const displayColumns = getDisplayColumns();
  const showTable = selectedSubs.size > 0;
  const importDisabled = selectedSubs.size !== 1;

  let importColumns = [];
  let selectedCategoryName = "";
  let selectedSubcategoryName = "";
  if (selectedSubs.size === 1) {
    const [subId] = [...selectedSubs];
    const cat = categories.find((c) =>
      Object.keys(subcategories[c._id] || {}).includes(subId)
    );
    if (cat) {
      selectedCategoryName = cat.name;
      const sub = subcategories[cat._id][subId];
      selectedSubcategoryName = sub.name;
      importColumns = sub.columns || [];
    }
  }

  const getAvailableFields = (currentCol) => {
    return importColumns.filter((field) => {
      return !Object.entries(mapping).some(
        ([col, mapped]) => mapped === field && col !== currentCol
      );
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 4,
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "bold",
                background: "linear-gradient(135deg, #6366f1, #ec4899)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Categories
            </Typography>
            <List component="nav" sx={{ maxHeight: 400, overflow: "auto" }}>
              {categories.map((cat) => (
                <div key={cat._id}>
                  <ListItemButton
                    onClick={() => toggleCategoryExpand(cat._id)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      "&:hover": {
                        bgcolor: "rgba(99, 102, 241, 0.1)",
                      },
                    }}
                  >
                    {expandedCategories.includes(cat._id) ? (
                      <MdExpandMore color="#6366f1" />
                    ) : (
                      <MdChevronRight color="#6366f1" />
                    )}
                    <ListItemText
                      primary={cat.name}
                      primaryTypographyProps={{
                        fontWeight: "medium",
                        color: "text.primary",
                      }}
                    />
                  </ListItemButton>
                  {expandedCategories.includes(cat._id) &&
                    Object.entries(subcategories[cat._id] || {}).map(
                      ([subId, sub]) => (
                        <ListItemButton
                          key={subId}
                          sx={{
                            pl: 4,
                            borderRadius: 2,
                            mb: 0.5,
                            "&:hover": {
                              bgcolor: "rgba(99, 102, 241, 0.05)",
                            },
                          }}
                          onClick={() => toggleSubSelect(subId)}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={selectedSubs.has(subId)}
                              tabIndex={-1}
                              disableRipple
                              sx={{
                                color: "#6366f1",
                                "&.Mui-checked": {
                                  color: "#6366f1",
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={sub.name}
                            primaryTypographyProps={{
                              fontSize: "0.9rem",
                              color: "text.secondary",
                            }}
                          />
                        </ListItemButton>
                      )
                    )}
                </div>
              ))}
            </List>
            <Divider sx={{ my: 2, opacity: 0.3 }} />
            <GlassButton
              variant="contained"
              color="primary"
              component={Link}
              to="/home/category-manager"
              fullWidth
              sx={{ mt: 2 }}
            >
              Manage Categories
            </GlassButton>
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mb: 3,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <GlassButton
                variant="contained"
                color="primary"
                startIcon={<MdAdd />}
                onClick={() => setOpenAdd(true)}
              >
                Add Contact
              </GlassButton>
              <GlassButton
                variant="contained"
                color="secondary"
                startIcon={<MdUploadFile />}
                onClick={() => setOpenImport(true)}
                disabled={importDisabled}
              >
                Import from Excel
              </GlassButton>
              <GlassButton
                variant="outlined"
                color="warning"
                startIcon={<MdClear />}
                onClick={() => setSelectedSubs(new Set())}
              >
                Clear Selection
              </GlassButton>
            </Box>

            {showTable ? (
              <>
                <Table
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: "rgba(99, 102, 241, 0.05)",
                      }}
                    >
                      {displayColumns.map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            fontWeight: "bold",
                            color: "text.primary",
                          }}
                        >
                          {col}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.map((contact, index) => (
                      <TableRow
                        key={contact._id}
                        sx={{
                          "&:hover": {
                            bgcolor: "rgba(99, 102, 241, 0.02)",
                            transform: "scale(1.01)",
                            transition: "all 0.2s ease-in-out",
                          },
                          bgcolor:
                            index % 2 === 0
                              ? "rgba(0,0,0,0.02)"
                              : "transparent",
                        }}
                      >
                        <TableCell sx={{ fontWeight: "medium" }}>
                          {contact.Name}
                        </TableCell>
                        <TableCell>{contact.category || "Unknown"}</TableCell>
                        <TableCell>
                          {contact.subCategory || "Unknown"}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<MdVisibility />}
                              onClick={() => handleViewContact(contact)}
                              size="small"
                            >
                              View
                            </Button>
                            <Button
                              variant="outlined"
                              color="success"
                              onClick={() => handleEditContactDialog(contact)}
                              size="small"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<MdDelete />}
                              onClick={() => handleDeleteContact(contact._id)}
                              size="small"
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        borderRadius: 2,
                        "&.Mui-selected": {
                          background:
                            "linear-gradient(135deg, #6366f1, #818cf8)",
                        },
                      },
                    }}
                  />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Select a subcategory to view contacts
                </Typography>
                <Typography variant="body2">
                  Choose from the categories panel to display contacts
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* View Contact Dialog */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "transparent",
            background: "linear-gradient(135deg, #6366f1, #818cf8)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          Contact Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedContact && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                {selectedContact.Name}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Category: {selectedContact.category || "Unknown"} | Subcategory:{" "}
                {selectedContact.subCategory || "Unknown"}
              </Typography>
              <Divider sx={{ my: 2, opacity: 0.3 }} />
              {Object.entries(selectedContact).map(([key, value]) => {
                if (
                  [
                    "_id",
                    "Category",
                    "Subcategory",
                    "category",
                    "categoryId",
                    "subCategory",
                    "subCategoryId",
                    "addedBy",
                    "createdOn",
                    "updatedOn",
                    "adderRole",
                  ].includes(key)
                )
                  return null;
                return (
                  <Box key={key} mb={2}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      fontWeight="medium"
                    >
                      {key}:
                    </Typography>
                    <Typography color="text.primary" sx={{ ml: 1 }}>
                      {value || "N/A"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <GlassButton
            onClick={() => setOpenView(false)}
            variant="contained"
            color="primary"
          >
            Close
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Add New Contact</DialogTitle>
        <DialogContent>
          <Select
            value={newContact.Subcategory}
            onChange={(e) => {
              const subId = e.target.value;
              const cat = categories.find((c) =>
                Object.keys(subcategories[c._id] || {}).includes(subId)
              );
              setNewContact({
                ...newContact,
                Category: cat?._id || "",
                Subcategory: subId,
                Name: "",
                Phone: "",
              });
            }}
            displayEmpty
            fullWidth
            sx={{ mt: 2 }}
          >
            <MenuItem value="" disabled>
              Select Subcategory
            </MenuItem>
            {categories.map((cat) =>
              Object.entries(subcategories[cat._id] || {}).map(
                ([subId, sub]) => (
                  <MenuItem key={subId} value={subId}>
                    {cat.name} / {sub.name}
                  </MenuItem>
                )
              )
            )}
          </Select>
          {newContact.Subcategory &&
            (() => {
              const cat = categories.find((c) =>
                Object.keys(subcategories[c._id] || {}).includes(
                  newContact.Subcategory
                )
              );
              const sub = subcategories[cat._id][newContact.Subcategory];
              return sub.columns.map((col) => (
                <TextField
                  key={col}
                  label={col}
                  value={newContact[col] || ""}
                  onChange={(e) =>
                    setNewContact({ ...newContact, [col]: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  required={col === "Name" || col === "Phone"}
                />
              ));
            })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <GlassButton
            onClick={() => setOpenAdd(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </GlassButton>
          <GlassButton
            onClick={handleAddContact}
            variant="contained"
            color="primary"
            disabled={!newContact.Subcategory}
          >
            Add
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Edit Contact</DialogTitle>
        <DialogContent>
          {editContact && (
            <>
              <Select
                value={editContact.Subcategory}
                onChange={(e) => {
                  const subId = e.target.value;
                  const cat = categories.find((c) =>
                    Object.keys(subcategories[c._id] || {}).includes(subId)
                  );
                  setEditContact({
                    ...editContact,
                    Category: cat?._id || "",
                    Subcategory: subId,
                  });
                }}
                displayEmpty
                fullWidth
                sx={{ mt: 2 }}
              >
                <MenuItem value="" disabled>
                  Select Subcategory
                </MenuItem>
                {categories.map((cat) =>
                  Object.entries(subcategories[cat._id] || {}).map(
                    ([subId, sub]) => (
                      <MenuItem key={subId} value={subId}>
                        {cat.name} / {sub.name}
                      </MenuItem>
                    )
                  )
                )}
              </Select>
              {editContact.Subcategory &&
                (() => {
                  const cat = categories.find((c) =>
                    Object.keys(subcategories[c._id] || {}).includes(
                      editContact.Subcategory
                    )
                  );
                  const sub = subcategories[cat._id][editContact.Subcategory];
                  return sub.columns.map((col) => (
                    <TextField
                      key={col}
                      label={col}
                      value={editContact[col] || ""}
                      onChange={(e) =>
                        setEditContact({
                          ...editContact,
                          [col]: e.target.value,
                        })
                      }
                      fullWidth
                      margin="normal"
                      required={col === "Name" || col === "Phone"}
                    />
                  ));
                })()}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <GlassButton
            onClick={() => setOpenEdit(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </GlassButton>
          <GlassButton
            onClick={handleEditContact}
            variant="contained"
            color="primary"
            disabled={!editContact?.Subcategory}
          >
            Save
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Custom Column Dialog */}
      <Dialog
        open={openCustomColumn}
        onClose={() => setOpenCustomColumn(false)}
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Add Custom Column</DialogTitle>
        <DialogContent>
          <TextField
            label="Custom Column Name"
            value={customColumn}
            onChange={(e) => setCustomColumn(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <GlassButton
            onClick={() => setOpenCustomColumn(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </GlassButton>
          <GlassButton
            onClick={handleAddCustomColumn}
            variant="contained"
            color="primary"
            disabled={!customColumn}
          >
            Add
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Import from Excel Dialog */}
      <Dialog
        open={openImport}
        onClose={() => setOpenImport(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Import from Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ position: "relative" }}>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                setIsFileLoading(true);
                const reader = new FileReader();
                reader.onload = (evt) => {
                  const bstr = evt.target.result;
                  const wb = XLSX.read(bstr, { type: "binary" });
                  const wsname = wb.SheetNames[0];
                  const ws = wb.Sheets[wsname];
                  const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                  setExcelData(data);
                  if (data.length > 0) {
                    const headers = data[0];
                    const initMap = {};
                    headers.forEach((h) => (initMap[h] = ""));
                    setMapping(initMap);
                  }
                  setIsFileLoading(false);
                };
                reader.readAsBinaryString(file);
              }}
              style={{
                marginBottom: "16px",
                padding: "8px",
                border: "2px dashed #6366f1",
                borderRadius: "8px",
                width: "100%",
              }}
            />
            {isFileLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
          {excelData.length > 0 && (
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(99, 102, 241, 0.05)" }}>
                  <TableCell>Excel Column</TableCell>
                  <TableCell>Map to Field</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(mapping).map((col) => (
                  <TableRow key={col}>
                    <TableCell sx={{ fontWeight: "medium" }}>{col}</TableCell>
                    <TableCell>
                      <Select
                        value={mapping[col]}
                        onChange={(e) => {
                          if (e.target.value === "custom") {
                            handleSelectCustomColumn(col);
                          } else {
                            setMapping({ ...mapping, [col]: e.target.value });
                          }
                        }}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="">None</MenuItem>
                        {getAvailableFields(col).map((field) => (
                          <MenuItem key={field} value={field}>
                            {field}{" "}
                            {field === "Name" || field === "Phone"
                              ? "(Required)"
                              : ""}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">Add Custom Column</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <GlassButtonLoading
            onClick={() => setOpenImport(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </GlassButtonLoading>
          <GlassButtonLoading
            onClick={handleImport}
            variant="contained"
            color="primary"
            disabled={excelData.length === 0 || isImporting}
            startIcon={isImporting ? <CircularProgress size={20} /> : null}
          >
            {isImporting ? "Importing..." : "Import"}
          </GlassButtonLoading>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContactDatabase;
